import axios from "axios";
import { message } from "antd";
import { formatDateTime } from "./date";

export const formatAmount = (amount, currency) =>
  amount === undefined || amount === null ? "-" : `${currency || ""} ${Number(amount).toFixed(2)}`;

// Same 6-day window as partner-dashboard's billing-details module (the merchant-facing Due
// Soon badge / R8 payment-reminder pop-up) -- kept in sync so Finance and the merchant see the
// same threshold, even though this is a separate frontend with its own copy of the constant.
const DUE_SOON_WINDOW_DAYS = 6;

// Purely presentational, mirrors partner-dashboard's getDisplayStatus (billing-details/helper.tsx)
// -- never persisted on the backend, computed fresh on every render. PAID/OVERDUE/VOID pass
// through as-is; ISSUED is split into UPCOMING vs DUE_SOON based on how close dueAt is.
export const getDisplayStatus = (statement) => {
  if (statement.status === "PAID") return "PAID";
  if (statement.status === "VOID") return "VOID";
  if (statement.status === "OVERDUE") return "OVERDUE";

  if (!statement.dueAt) return "UPCOMING";
  const daysUntilDue = Math.ceil((new Date(statement.dueAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return daysUntilDue <= DUE_SOON_WINDOW_DAYS ? "DUE_SOON" : "UPCOMING";
};

export const DISPLAY_STATUS_COLORS = {
  OVERDUE: "red",
  DUE_SOON: "orange",
  UPCOMING: "blue",
  PAID: "green",
  VOID: "default",
};

export const DISPLAY_STATUS_LABELS = {
  OVERDUE: "Overdue",
  DUE_SOON: "Due Soon",
  UPCOMING: "Upcoming",
  PAID: "Paid",
  VOID: "Void",
};

export const downloadStatementPdf = (statementId) => {
  axios
    .get(`/api/billing-statements/${statementId}/pdf`)
    .then((res) => window.open(res.data.url))
    .catch((error) => message.error(error.response?.data?.message || error.message));
};

export const downloadStatementExcel = (statementId) => {
  axios
    .get(`/api/billing-statements/${statementId}/excel`)
    .then((res) => window.open(res.data.url))
    .catch((error) => message.error(error.response?.data?.message || error.message));
};

// Charge types as stored on BillingCharge (see BILLING_CHARGE_TYPES in the API). Insurance
// rolls into "Additional"; Weight Dispute rolls into "Freight" (it's a correction to that same
// consignment's freight cost, and BillingStatement.originalAmount sums every charge type
// including WEIGHT_DISPUTE, so folding it into Freight here keeps the itemised total
// reconciling with originalAmount instead of silently dropping it). Anything not matched here
// is silently dropped from the pivot.
const CHARGE_TYPE = {
  FREIGHT: "FREIGHT",
  WEIGHT_DISPUTE: "WEIGHT_DISPUTE",
  COD_FEE: "COD_FEE",
  RETURN_FEE: "RETURN_FEE",
  ADDITIONAL_SERVICE: "ADDITIONAL_SERVICE",
  INSURANCE: "INSURANCE",
  NDR: "NEXT_DAY_REMITTANCE",
};

// Pivots raw BillingCharge rows (one row per charge type per event) into one row per
// consignment note, matching the itemised invoice table format. Shared by the global Billing
// Statements page and the per-merchant Billing tab on User Details.
export const pivotChargesByConsignment = (charges) => {
  const grouped = {};

  charges.forEach((charge) => {
    const key = charge.consignNo;
    if (!grouped[key]) {
      grouped[key] = {
        consignNo: charge.consignNo,
        serviceProvider: charge.serviceProvider,
        pickupDate: charge.pickupDate,
        region: charge.region,
        chargeableWeight: null,
        freight: 0,
        cod: 0,
        returnFee: 0,
        additional: 0,
        ndr: 0,
      };
    }

    const row = grouped[key];
    row.pickupDate = row.pickupDate || charge.pickupDate;
    row.region = row.region || charge.region;

    // Weight disputes only ever ratchet an order's weight upward (see webHookWeight), so
    // between the original freight charge's weight and a later dispute's weight, the larger
    // one is the correct final weight -- never just "whichever charge came first."
    const chargeWeight = charge.chargeableWeight != null ? Number(charge.chargeableWeight) : null;
    if (chargeWeight != null && (row.chargeableWeight == null || chargeWeight > row.chargeableWeight)) {
      row.chargeableWeight = chargeWeight;
    }

    const amount = Number(charge.amount || 0);
    switch (charge.chargeType) {
      case CHARGE_TYPE.FREIGHT:
      case CHARGE_TYPE.WEIGHT_DISPUTE:
        row.freight += amount;
        break;
      case CHARGE_TYPE.COD_FEE:
        row.cod += amount;
        break;
      case CHARGE_TYPE.RETURN_FEE:
        row.returnFee += amount;
        break;
      case CHARGE_TYPE.ADDITIONAL_SERVICE:
      case CHARGE_TYPE.INSURANCE:
        row.additional += amount;
        break;
      case CHARGE_TYPE.NDR:
        row.ndr += amount;
        break;
      default:
        break;
    }
  });

  return Object.values(grouped).map((row) => ({
    ...row,
    actualPrice: row.freight + row.cod + row.returnFee + row.additional + row.ndr,
  }));
};

export const chargeColumns = [
  {
    title: "Pickup Date",
    dataIndex: "pickupDate",
    key: "pickupDate",
    render: (d) => (d ? formatDateTime(d) : "-"),
  },
  {
    title: "Consignment Note",
    key: "consignNo",
    render: (_, r) => `${r.consignNo}${r.serviceProvider ? ` (${r.serviceProvider})` : ""}`,
  },
  { title: "Region", dataIndex: "region", key: "region", render: (v) => v || "-" },
  {
    title: "Weight (KG)",
    dataIndex: "chargeableWeight",
    key: "chargeableWeight",
    render: (w) => (w || w === 0 ? Number(w).toFixed(3) : "-"),
  },
  { title: "Freight (RM)", dataIndex: "freight", key: "freight", render: (v) => v.toFixed(2) },
  { title: "COD (RM)", dataIndex: "cod", key: "cod", render: (v) => v.toFixed(2) },
  { title: "Return (RM)", dataIndex: "returnFee", key: "returnFee", render: (v) => v.toFixed(2) },
  { title: "Additional (RM)", dataIndex: "additional", key: "additional", render: (v) => v.toFixed(2) },
  { title: "NDR (RM)", dataIndex: "ndr", key: "ndr", render: (v) => v.toFixed(2) },
  { title: "Actual Price (RM)", dataIndex: "actualPrice", key: "actualPrice", render: (v) => v.toFixed(2) },
];
